// ./src/hooks/useHomeOverview.ts
//
// Everything the Home page reads, in one place (ROADMAP A2b #50).
//
// ⚠⚠ THE COUNTS ARE `result.total`, NEVER `data.length`. Each attention read asks for ONE row and
// wants the number of rows there ARE — a length fallback would report "1 waiting" forever. The rule
// lives in `readAttentionCount`, which is pure so `npm run check-home` can run it.
//
// ⚠ NO SOURCE MAY TAKE ANOTHER DOWN. Each read catches its own failure and reports `failed`, so one
// module a host has never used cannot blank the card — and, just as importantly, cannot be mistaken
// for an empty queue. `summarizeAttention` refuses to say "all caught up" unless every source
// answered.
//
// ⚠ A CREW SESSION HAS NO ACCOUNT. Every account-wide list below fails closed to the server's DENY
// sentinel and comes back empty, which would render as a green "all caught up" over an account the
// crew member cannot see. So a crew session never runs these at all — `enabled` is false.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { OpResult, OP_RESULT_CODES } from "@sdflc/api-helpers";

import {
  EventGuestService,
  EventGuestStats,
  HomeAttentionItem,
  HomeAttentionPayload,
  HomeAttentionService,
  HomeWeather,
  StripeConnectService,
  StripeConnectStatus,
  Task,
  TaskService,
  TASK_STATUS,
} from "@/utils/api";
import {
  AttentionItem,
  AttentionSummary,
  EMPTY_SERVER_ATTENTION,
  ServerAttention,
  SpotlightPick,
  pickSpotlightEvent,
  summarizeAttention,
} from "@/utils/homeOverview";
import { splitEvents } from "@/components/features/Events/eventListHelpers";
import { Event } from "@/utils/api";

import { useEvents } from "./useEvents";
import { useRefreshOnWriteTimeout } from "./useRefreshOnWriteTimeout";

/** How many upcoming tasks the card shows. */
export const HOME_TASK_LIMIT = 5;

/** Tasks that are still somebody's problem. */
const OPEN_TASK_STATUSES = [TASK_STATUS.TODO, TASK_STATUS.IN_PROGRESS, TASK_STATUS.PAUSED];

/**
 * Turn the server query into the shape the shared rule reads.
 *
 * ⚠⚠ A QUERY THAT DID NOT COME BACK IS `failed`, NEVER AN EMPTY ANSWER. An empty `items` list means
 * "nothing is due in the next week"; a failed read means "we do not know whether anything is". They
 * are the same pixels and opposite facts, and only one of them may produce a green tick.
 */
const readServerAttention = (result: OpResult | null): ServerAttention & { weather: HomeWeather | null } => {
  if (!result || result.code !== OP_RESULT_CODES.OK) {
    return { ...EMPTY_SERVER_ATTENTION, weather: null, failed: true };
  }

  const payload = (result.getDataFirst() as HomeAttentionPayload) ?? null;

  if (!payload) {
    return { ...EMPTY_SERVER_ATTENTION, weather: null, failed: true };
  }

  const items: AttentionItem[] = (payload.items ?? [])
    .filter((row): row is HomeAttentionItem => !!row?.kind)
    .map((row) => ({
      kind: String(row.kind),
      source: "server" as const,
      severity: row.severity || "info",
      count: Number(row.count ?? 0),
      eventId: row.eventId ?? null,
      eventTitle: row.eventTitle ?? null,
      at: row.at ?? null,
      who: row.who ?? null,
      amount: row.amount ?? null,
      currencyCode: row.currencyCode ?? null,
      capacity: row.capacity ?? null,
      // ⚠ THIS MAP IS EXPLICIT, SO A FIELD THE SERVER ADDS ARRIVES AS `undefined` UNTIL IT IS LISTED
      // HERE — the query selects it, the type declares it, the renderer reads it, and the line it
      // was for just never appears. Four layers agreeing about a field nothing carries.
      threshold: row.threshold ?? null,
      coverage: row.coverage ?? null,
      reasons: row.reasons ?? null,
    }));

  return {
    items,
    // ⚠ Not part of `ServerAttention`'s rule — the forecast decides nothing about the all-clear, so
    // it stays out of the pure module and is carried alongside.
    weather: payload.weather ?? null,
    moreCount: Number(payload.moreCount ?? 0),
    failedKinds: Array.isArray(payload.failedKinds) ? payload.failedKinds.filter(Boolean) : [],
    answered: true,
    failed: false,
  };
};

export interface UseHomeOverviewReturn {
  /** The forecast for the next event's venue, already cached server-side. */
  homeWeather: HomeWeather | null;
  events: Event[];
  upcoming: Event[];
  eventsLoading: boolean;
  /**
   * ⚠ Whether the FIRST events read has COME BACK — not the same as "not loading". Both reads here
   * are gated on `enabled`, which is false until `me()` answers, so on the first renders nothing
   * has been asked for: loading false and the collections empty. A widget deciding on that pair
   * says "Nothing scheduled" / "Nothing else on the calendar" before anything has been read.
   */
  eventsLoaded: boolean;
  eventsError: OpResult | null;

  spotlight: SpotlightPick;
  spotlightStats: EventGuestStats | null;

  serverAttention: ServerAttention;
  attentionSummary: AttentionSummary;

  tasks: Task[];
  tasksLoading: boolean;
  /** Whether the FIRST tasks read has come back — see `eventsLoaded`. */
  tasksLoaded: boolean;

  /** `null` until the payout status is known — and unknown is never "done". */
  payoutsReady: boolean | null;

  /** A re-read is in flight while the previous answers stay on screen. */
  refreshing: boolean;

  loading: boolean;
  reload: () => void;
}

export interface UseHomeOverviewOptions {
  /**
   * False for a crew session (no account), and false until a user is resolved.
   *
   * ⚠ A COLD LOAD RESOLVES THE USER ASYNCHRONOUSLY. Firing these before `me()` answers sends three
   * unauthenticated list calls on every page load, which come back refused and render as three
   * failed sources on the first screen of the product. `useEvents` already waits; so does this.
   */
  enabled: boolean;
  /** Skip the payout read when the checklist is not on screen to want it. */
  wantsPayoutStatus: boolean;
}

export const useHomeOverview = (options: UseHomeOverviewOptions): UseHomeOverviewReturn => {
  const { enabled, wantsPayoutStatus } = options;
  const { events, loading: eventsLoading, isLoaded: eventsLoaded, error: eventsError, reload: reloadEvents } = useEvents();

  const [refreshing, setRefreshing] = useState(false);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [tasksLoaded, setTasksLoaded] = useState(false);
  const [spotlightStats, setSpotlightStats] = useState<EventGuestStats | null>(null);
  const [payoutsReady, setPayoutsReady] = useState<boolean | null>(null);
  const [serverAttention, setServerAttention] = useState<ServerAttention>(EMPTY_SERVER_ATTENTION);
  const [homeWeather, setHomeWeather] = useState<HomeWeather | null>(null);

  const callIdRef = useRef(0);
  const statsCallIdRef = useRef(0);

  const upcoming = useMemo(() => splitEvents(events, "").upcoming, [events]);
  const spotlight = useMemo(() => pickSpotlightEvent(upcoming), [upcoming]);
  const spotlightId = spotlight.event?.id ?? null;

  const attentionSummary = useMemo(() => summarizeAttention(serverAttention), [serverAttention]);

  const loadAttention = useCallback(async () => {
    if (!enabled) {
      return;
    }

    const myCall = ++callIdRef.current;
    // ⚠ THE PREVIOUS NUMBERS STAY ON SCREEN. Blanking them here would make every refresh flash the
    // card back to skeletons — and, worse, a superseded call would have blanked a newer call's
    // answers before the race guard below ever ran. The header spinner is the "working" signal,
    // exactly as it is on the event dashboard.
    setRefreshing(true);
    setTasksLoading(true);

    /**
     * ⚠ EACH READ CATCHES ITS OWN FAILURE. `Promise.all` rejects on the first rejection, which would
     * turn one unavailable module into a page that reports nothing — and reporting nothing is this
     * page's way of saying "you are up to date".
     *
     * ⓘ The guest / sponsor / volunteer counts used to be read here. They are in `myHomeAttention`
     * now, grouped per event like every other row, so one row on the card means one thing.
     */
    const [openTasks, serverSide] = await Promise.all([
      // ⚠ `scheduledAt ASC` IS LOAD-BEARING, not a nicety. There is no due-date filter on the
      // server, so this page IS the filter: ascending puts the most overdue first and (Postgres
      // orders NULLS LAST on ASC) sinks undated tasks below the dated ones. Flip it to DESC and a
      // five-row card fills with tasks that are not due for months while today's sit below the cut.
      TaskService.list({ taskStatus: [...OPEN_TASK_STATUSES] }, { page: 1, pageSize: HOME_TASK_LIMIT }, [
        { name: "scheduledAt", order: "ASC" },
      ]).catch(() => null),
      // ⚠ Everything else. Every count in it is a DATE WINDOW or a per-event grouping that no filter
      // in this API can express — see `HomeAttentionService`.
      HomeAttentionService.mine().catch(() => null),
    ]);

    if (myCall !== callIdRef.current) {
      return; // a newer load superseded this one
    }

    setRefreshing(false);
    const server = readServerAttention(serverSide);

    setServerAttention(server);
    setHomeWeather(server.weather);

    /**
     * ⚠ NAME WHAT WENT QUIET, IN THE CONSOLE. The host is told only "some checks couldn't be loaded",
     * which is the right message for them and useless for diagnosing it — the first time this fired
     * in production it took a schema read to find out that ONE fragment was naming a column that had
     * not existed for months. The kinds are cheap to print and they name the bug.
     */
    if (server.failedKinds.length > 0) {
      console.warn('[home] attention sources that could not answer:', server.failedKinds.join(', '));
    }

    setTasksLoading(false);
    // ⚠ Whatever came back — a read that FAILED has still come back, and leaving the flag false
    // would hold the skeleton on screen for ever instead of showing what is known.
    setTasksLoaded(true);
    setTasks(
      openTasks && openTasks.code === OP_RESULT_CODES.OK ? ((openTasks.getData() as Task[]) ?? []) : [],
    );
  }, [enabled]);

  const loadPayouts = useCallback(async () => {
    if (!enabled || !wantsPayoutStatus) {
      return;
    }

    const result = await StripeConnectService.getStatus().catch(() => null);

    if (!result || result.code !== OP_RESULT_CODES.OK) {
      // ⚠ Left as `null` — UNKNOWN, not "not set up" and certainly not "done". `buildChecklist`
      // treats anything but an explicit true as outstanding.
      setPayoutsReady(null);

      return;
    }

    const status = (result.getDataFirst() as StripeConnectStatus) ?? null;

    setPayoutsReady(status ? status.payoutsEnabled === true : false);
  }, [enabled, wantsPayoutStatus]);

  const loadSpotlightStats = useCallback(async () => {
    if (!enabled || !spotlightId) {
      setSpotlightStats(null);

      return;
    }

    const myCall = ++statsCallIdRef.current;
    const result = await EventGuestService.stats({ eventId: spotlightId }).catch(() => null);

    if (myCall !== statsCallIdRef.current) {
      return;
    }

    setSpotlightStats(
      result && result.code === OP_RESULT_CODES.OK ? ((result.getDataFirst() as EventGuestStats) ?? null) : null,
    );
  }, [enabled, spotlightId]);

  useEffect(() => {
    loadAttention();
  }, [loadAttention]);

  useEffect(() => {
    loadPayouts();
  }, [loadPayouts]);

  useEffect(() => {
    loadSpotlightStats();
  }, [loadSpotlightStats]);

  const reload = useCallback(() => {
    reloadEvents();
    loadAttention();
    loadPayouts();
    loadSpotlightStats();
  }, [reloadEvents, loadAttention, loadPayouts, loadSpotlightStats]);

  // ROADMAP #131 — a save that timed out may still have landed: re-read what is shown.
  useRefreshOnWriteTimeout(reload, tasksLoaded);

  return {
    events,
    upcoming,
    eventsLoading,
    eventsLoaded,
    eventsError,
    spotlight,
    spotlightStats,
    homeWeather,
    serverAttention,
    attentionSummary,
    tasks,
    tasksLoading,
    tasksLoaded,
    payoutsReady,
    refreshing,
    loading: eventsLoading || attentionSummary.loading,
    reload,
  };
};
